import { QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { BrowserRouter } from 'react-router';

import { AuthProvider } from './auth/AuthProvider.js';
import { BrandingProvider } from './branding/BrandingProvider.js';
import { ToastProvider } from './components/Toast.js';
import { JobDrawerProvider } from './lib/JobDrawerContext.js';
import { createQueryClient } from './lib/queryClient.js';
import { AppRoutes } from './router.js';

/**
 * Provider order is load-bearing.
 *
 * `BrandingProvider` is outermost because `AuthProvider` calls into it —
 * `/auth/me` returns the authoritative brand and adopting it is how a
 * shared-host tenant switch stays correct. `QueryClientProvider` sits above
 * anything using a hook from TanStack Query, which by now is most of the app.
 */
export function App(): ReactElement {
  // Created once per mount rather than at module scope, so a test can render
  // two apps without them sharing a cache.
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <BrandingProvider>
        <AuthProvider>
          <ToastProvider>
            <JobDrawerProvider>
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
            </JobDrawerProvider>
          </ToastProvider>
        </AuthProvider>
      </BrandingProvider>
    </QueryClientProvider>
  );
}
